import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ProblemError, type components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";
import { vereist } from "@jbnl/shared-ui";

const PERIODES =
  "/api/seizoenen/{seizoenId}/inschrijfperiodes/korpsen" as const;
const INSCHRIJVING =
  "/api/inschrijfperiodes/{inschrijfperiodeId}/korpsen/{korpsId}/inschrijving" as const;
const PLOEGEN =
  "/api/inschrijfperiodes/{inschrijfperiodeId}/korpsen/{korpsId}/ploegen" as const;
const PLOEG =
  "/api/inschrijfperiodes/{inschrijfperiodeId}/korpsen/{korpsId}/ploegen/{ploegId}" as const;

export type Inschrijving = components["schemas"]["InschrijvingDto"];
export type PloegInschrijving = components["schemas"]["PloegInschrijvingDto"];
export type VoorkeurInput = components["schemas"]["VoorkeurInput"];

/**
 * What a korps says about one ploeg. One shape for entering and for changing, because the korps
 * fills in the same form either way - the API takes the same body on both routes.
 */
export interface InschrijvingVelden {
  /** Empty means "use the derived name", which is what the API does with it. */
  name: string;
  klasseIds: string[];
  voorkeuren: VoorkeurInput[];
  /**
   * The samenvoorkeur: this ploeg on the same wedstrijddag as the korps's others (ADR-0052).
   * `false` is "geen voorkeur" and asks the planner for nothing.
   */
  wilSamenOpEenDag: boolean;
  /** The ploeg comes with a VR vehicle other ploegen share (ADR-0077). */
  deeltVrVoertuig: boolean;
}

/**
 * The API serialises `DatePreferenceKind` as a number, so the SPA carries the names rather than
 * repeating the digits at every call site.
 */
export const VOORKEUR = { voorkeur: 1, geenVoorkeur: 2 } as const;

/**
 * Keyed on the periode, not the seizoen: a korps answering two windows must not share one cache
 * entry. Keys are written by hand - see `usePloegen` for why.
 */
export const inschrijvingKey = (inschrijfperiodeId: string, korpsId: string) =>
  ["inschrijving", inschrijfperiodeId, korpsId] as const;

/** RFC 7807, so a refusal carries a Dutch sentence; the status code is the fallback. */
function problem(error: unknown, response: Response, fallback: string) {
  const veld = (error as { errors?: Record<string, string[]> } | undefined)
    ?.errors;
  if (veld) {
    const eerste = Object.values(veld).flat()[0];
    if (eerste) return new ProblemError(eerste);
  }

  // The type comes along: a caller that only prints the sentence ignores it, and the one that
  // has to put the sentence somewhere particular has something other than prose to test.
  const { title, type } = (error ?? {}) as { title?: string; type?: string };
  return new ProblemError(title ?? `${fallback} (${response.status}).`, type);
}

/**
 * The windows a korps may enter under. Not gated and not korps-scoped - every korps sees the
 * same calendar, and no read is ever refused because a window has shut (ADR-0031).
 */
export function useKorpsPeriodes(seizoenId: string | undefined) {
  return useQuery({
    queryKey: ["inschrijfperiodes", "korpsen", seizoenId ?? ""] as const,
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error } = await api.GET(PERIODES, {
            params: { path: { seizoenId } },
          });
          if (error)
            throw new Error(i18n.t("inschrijving:errors.periodesOphalen"));
          return data ?? [];
        },
  });
}

export function useInschrijving(
  inschrijfperiodeId: string | undefined,
  korpsId: string | undefined,
) {
  return useQuery({
    queryKey: inschrijvingKey(inschrijfperiodeId ?? "", korpsId ?? ""),
    queryFn:
      !inschrijfperiodeId || !korpsId
        ? skipToken
        : async () => {
            const { data, error } = await api.GET(INSCHRIJVING, {
              params: { path: { inschrijfperiodeId, korpsId } },
            });
            if (error) throw new Error(i18n.t("inschrijving:errors.ophalen"));
            return data;
          },
  });
}

/**
 * One hook for the four writes, sharing the invalidation: a save landing while a withdrawal is
 * in flight cannot leave the two halves of the screen disagreeing.
 */
export function useInschrijvingActies(
  inschrijfperiodeId: string | undefined,
  korpsId: string | undefined,
) {
  const queryClient = useQueryClient();

  // Read when a mutation fires, not while rendering: the page mounts before the periode and the
  // korps have arrived, and a hook that throws on that first render takes the whole screen down
  // instead of letting it show its skeleton.
  const pad = () => ({
    inschrijfperiodeId: vereist(inschrijfperiodeId, "inschrijfperiodeId"),
    korpsId: vereist(korpsId, "korpsId"),
  });

  const onSuccess = () =>
    queryClient.invalidateQueries({
      queryKey: inschrijvingKey(inschrijfperiodeId ?? "", korpsId ?? ""),
    });

  // Entering a ploeg carries its klassen and voorkeuren, so it is one request rather than a
  // create followed by a save. The klassen are the half most likely to be refused, and a
  // two-step entry would leave an empty ploeg in the list every time the second step failed.
  const aanmelden = useMutation({
    mutationFn: async (velden: InschrijvingVelden) => {
      const { data, error, response } = await api.POST(PLOEGEN, {
        params: { path: pad() },
        body: velden,
      });
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("inschrijving:errors.inschrijven"),
        );
      return data;
    },
    onSuccess,
  });

  const opslaan = useMutation({
    mutationFn: async (input: { ploegId: string } & InschrijvingVelden) => {
      const { data, error, response } = await api.PUT(PLOEG, {
        params: { path: { ...pad(), ploegId: input.ploegId } },
        body: {
          name: input.name,
          klasseIds: input.klasseIds,
          voorkeuren: input.voorkeuren,
          wilSamenOpEenDag: input.wilSamenOpEenDag,
          deeltVrVoertuig: input.deeltVrVoertuig,
        },
      });
      if (!data)
        throw problem(error, response, i18n.t("inschrijving:errors.opslaan"));
      return data;
    },
    onSuccess,
  });

  const intrekken = useMutation({
    mutationFn: async (ploegId: string) => {
      const { error, response } = await api.DELETE(PLOEG, {
        params: { path: { ...pad(), ploegId } },
      });
      if (error || !response.ok)
        throw problem(error, response, i18n.t("inschrijving:errors.intrekken"));
    },
    onSuccess,
  });

  return { aanmelden, opslaan, intrekken };
}
